import { currentAuthEpoch } from '@/utils/authRecovery'
import { defineStore } from 'pinia'
import type {
  INotification,
  INotificationDto,
  TNotificationFilter,
  TNotificationPriority,
} from '../types/notification.types'
import { notificationsApi } from '../services/notificationsApi'
import { getNotificationVisual } from '../composables/useNotificationType'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { getApiError } from '@/composables/getApiError'
import type { IRealtimePayload } from '@/modules/centrifuge/types/IRealtimePayload'

/**
 * Извлекает значение query-параметра `cursor` из абсолютного URL пагинации,
 * который приходит от бека в полях `next`/`previous`. Возвращает null если нет.
 */
function extractCursor(url: string | null): string | null {
  if (!url) return null
  try {
    const parsed = new URL(url)

    return parsed.searchParams.get('cursor')
  } catch {
    return null
  }
}

/**
 * Маппинг сырого DTO уведомления в локальную VM фронта.
 * Чистая функция, экспортируется — переиспользуется в Centrifuge-обработчике realtime.
 */
export function mapDtoToVm(dto: INotificationDto): INotification {
  return {
    id: dto.id,
    messageType: dto.message_type,
    title: dto.title ?? '',
    message: dto.message ?? '',
    format: dto.format ?? 'default',
    flow: dto.flow,
    buttons: Array.isArray(dto.buttons) ? dto.buttons : [],
    sourceType: dto.source_type ?? null,
    urlProps: dto.url_props ?? null,
    createdAt: dto.created_at,
    readAt: dto.read_at,
    isSeen: dto.is_read,
    priority: dto.priority,
  }
}

/**
 * Маппинг активного фильтра сайдбара в параметры бекенд-запроса.
 * Значения `starred` и `trash` транслируются как «без серверного фильтра» — это
 * фронтовые представления (избранное хранится локально, корзина — заглушка).
 */
function filterToApiParams(filter: TNotificationFilter): {
  is_read?: boolean
  content_type_names?: string[]
} {
  switch (filter) {
    case 'unread':
      return { is_read: false }
    case 'adverse_event':
      return { content_type_names: ['adverse'] }
    case 'ticket':
      return { content_type_names: ['ticket'] }
    case 'calendar_event':
      return { content_type_names: ['calendar'] }
    default:
      return {}
  }
}

/**
 * Подходит ли DTO под активный фильтр сайдбара — нужно при добавлении новых
 * уведомлений через realtime, чтобы не показывать «чужие» в текущем списке.
 */
function dtoMatchesFilter(dto: INotificationDto, filter: TNotificationFilter): boolean {
  switch (filter) {
    case 'all':
      return true
    case 'unread':
      return !dto.is_read
    case 'starred':
    case 'trash':
      return false
    case 'adverse_event':
      return dto.message_type === 'adverse_event' || dto.message_type === 'global_adverse_event'
    case 'ticket':
      return dto.message_type === 'ticket' || dto.message_type === 'global_ticket'
    case 'calendar_event':
      return dto.message_type === 'calendar_event'
    default:
      return true
  }
}

const STARRED_STORAGE_KEY = 'materio.notifications.starredIds'
const PAGE_SIZE_STORAGE_KEY = 'tableItemsPerPage'
const PAGE_SIZE_OPTIONS = [25, 50, 100]
const DEFAULT_PAGE_SIZE = 25

function loadItemsPerPage(): number {
  if (typeof window === 'undefined') return DEFAULT_PAGE_SIZE
  try {
    const raw = window.localStorage.getItem(PAGE_SIZE_STORAGE_KEY)
    if (!raw) return DEFAULT_PAGE_SIZE
    const parsed = JSON.parse(raw)
    const val = Number(parsed?.notifications)

    return PAGE_SIZE_OPTIONS.includes(val) ? val : DEFAULT_PAGE_SIZE
  } catch {
    return DEFAULT_PAGE_SIZE
  }
}

function loadStarred(): number[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(STARRED_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)

    return Array.isArray(parsed) ? parsed.filter((x): x is number => typeof x === 'number') : []
  } catch {
    return []
  }
}

function saveStarred(ids: number[]): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STARRED_STORAGE_KEY, JSON.stringify(ids))
  } catch {
    // приватный режим / переполнение — тихо
  }
}

let activeFetchEpoch = -1
let activeFetchPromise: Promise<void> | null = null

export const useNotificationStore = defineStore('notificationStore', {
  state: () => ({
    items: [] as INotification[],
    isLoading: false,
    nextCursor: null as string | null,
    prevCursor: null as string | null,
    currentPage: 1,
    itemsPerPage: loadItemsPerPage(),
    totalCount: 0,
    filter: 'all' as TNotificationFilter,
    priority: null as TNotificationPriority | null,
    search: '',
    selectedIds: new Set<number>(),
    starredIds: new Set<number>(loadStarred()),
    /** Общий счётчик непрочитанных из бек-ответа (поле count при фильтре is_read=false). */
    totalUnread: null as number | null,
  }),

  getters: {
    /**
     * Число непрочитанных в текущем загруженном списке.
     * Для глобального счётчика на колокольчике используется наряду с totalUnread —
     * стор предпочитает totalUnread когда доступен (даёт точный счёт по всему API).
     */
    unreadCount: state => state.totalUnread ?? state.items.filter(n => !n.isSeen).length,
    hasUnread(): boolean {
      return this.unreadCount > 0
    },

    /**
     * Уведомления после применения фронтовых фильтров (приоритет, поиск, избранное).
     * Если активный фильтр — `starred`, оставляем только звёздные.
     */
    filteredItems(state): INotification[] {
      let list = state.items

      if (state.filter === 'starred') {
        list = list.filter(item => state.starredIds.has(item.id))
      }

      if (state.search.trim()) {
        const q = state.search.trim().toLowerCase()
        list = list.filter(item =>
          item.title.toLowerCase().includes(q)
          || item.message.toLowerCase().includes(q),
        )
      }

      return list
    },

    /** Признак — есть ли активный фильтр поверх «Все»: показать кнопку сброса и т.п. */
    hasActiveFilter(state): boolean {
      return state.filter !== 'all' || state.priority !== null || state.search.trim().length > 0
    },
  },

  actions: {
    /**
     * Загружает одну страницу списка.
     * - reset=true (default) — первая страница, без cursor.
     * - cursor — opaque-курсор next/previous для соседней страницы.
     */
    async fetchNotifications(options: { reset?: boolean; cursor?: string | null } = {}): Promise<void> {
      const epoch = currentAuthEpoch()
      if (activeFetchPromise && activeFetchEpoch === epoch) {
        return activeFetchPromise
      }

      const { reset = true, cursor = null } = options
      this.isLoading = true
      // Keep the last successful page while recovery retries an unavailable API.

      activeFetchEpoch = epoch
      activeFetchPromise = (async () => {
        try {
          const params: Parameters<typeof notificationsApi.list>[0] = {
            ...filterToApiParams(this.filter),
            page_size: this.itemsPerPage,
          }
          if (this.priority) {
            params.priority = [this.priority]
          }
          if (cursor) {
            params.cursor = cursor
          }

          const { data } = await notificationsApi.list(params)
          if (epoch !== currentAuthEpoch()) return
          const mapped = (data.results ?? []).map(mapDtoToVm)

          this.items = mapped
          this.nextCursor = extractCursor(data.next)
          this.prevCursor = extractCursor(data.previous)
          const count = Number(data.count)
          this.totalCount = Number.isFinite(count) ? count : mapped.length

          if (reset) {
            this.currentPage = 1
          }

          if (typeof data.unread_count === 'number') {
            this.totalUnread = data.unread_count
          }
        } finally {
          if (activeFetchEpoch === epoch) {
            this.isLoading = false
            activeFetchPromise = null
          }
        }
      })()

      return activeFetchPromise
    },

    async goToNextPage(): Promise<void> {
      if (!this.nextCursor || this.isLoading) return
      this.selectedIds = new Set()
      await this.fetchNotifications({ reset: false, cursor: this.nextCursor })
      this.currentPage += 1
    },

    async goToPrevPage(): Promise<void> {
      if (!this.prevCursor || this.isLoading) return
      this.selectedIds = new Set()
      await this.fetchNotifications({ reset: false, cursor: this.prevCursor })
      this.currentPage = Math.max(1, this.currentPage - 1)
    },

    async goToFirstPage(): Promise<void> {
      this.selectedIds = new Set()
      await this.fetchNotifications({ reset: true })
    },

    async setItemsPerPage(size: number): Promise<void> {
      if (this.itemsPerPage === size) return
      this.itemsPerPage = size
      this.selectedIds = new Set()
      await this.fetchNotifications({ reset: true })
    },

    /** Сменить активный фильтр сайдбара и перезагрузить список. */
    async setFilter(filter: TNotificationFilter): Promise<void> {
      if (this.filter === filter) return
      this.filter = filter
      this.selectedIds = new Set()
      await this.fetchNotifications({ reset: true })
    },

    /** Установить приоритет (или сбросить, передав null). */
    async setPriority(priority: TNotificationPriority | null): Promise<void> {
      if (this.priority === priority) return
      this.priority = priority
      this.selectedIds = new Set()
      await this.fetchNotifications({ reset: true })
    },

    /** Установить поисковую строку. Фронтовая фильтрация — без запроса. */
    setSearch(query: string): void {
      this.search = query
    },

    // ===== Выделение и массовые действия =====

    toggleSelected(id: number): void {
      if (this.selectedIds.has(id)) {
        this.selectedIds.delete(id)
      } else {
        this.selectedIds.add(id)
      }
    },

    selectAll(): void {
      this.selectedIds = new Set(this.filteredItems.map(item => item.id))
    },

    clearSelection(): void {
      this.selectedIds = new Set()
    },

    /**
     * Массовое удаление выбранных через DELETE /bulk-destroy/?ids=.
     * При ошибке — toast, список не трогаем.
     */
    async bulkRemove(): Promise<void> {
      const ids = Array.from(this.selectedIds)
      if (!ids.length) return

      const unreadRemoved = ids.filter(id => {
        const item = this.items.find(n => n.id === id)

        return item !== undefined && !item.isSeen
      }).length

      try {
        await notificationsApi.bulkDestroy(ids)
        const idSet = new Set(ids)
        this.items = this.items.filter(n => !idSet.has(n.id))
        this.totalCount = Math.max(0, this.totalCount - ids.length)
        if (this.totalUnread !== null) {
          this.totalUnread = Math.max(0, this.totalUnread - unreadRemoved)
        }
        this.clearSelection()
        if (!this.items.length && this.totalCount > 0) {
          await this.goToFirstPage()
        }
      } catch (error) {
        useFeedbackStore().showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось удалить уведомления'),
        })
      }
    },

    /**
     * Массовое пометить выбранные прочитанными. Параллельные POST с Promise.allSettled —
     * при ошибке части запросов показываем toast.
     */
    async bulkMarkRead(): Promise<void> {
      const ids = Array.from(this.selectedIds).filter(id => {
        const item = this.items.find(n => n.id === id)

        return item !== undefined && !item.isSeen
      })
      if (!ids.length) return
      const results = await Promise.allSettled(ids.map(id => this.markOneAsRead(id)))
      this.clearSelection()
      const failed = results.filter(r => r.status === 'rejected').length
      if (failed > 0) {
        useFeedbackStore().showToast({
          type: 'error',
          message: `Не удалось отметить прочитанными ${failed} из ${ids.length} уведомлений`,
        })
      }
    },

    // ===== Избранное (фронт-only до выкатки бека) =====

    toggleStarred(id: number): void {
      if (this.starredIds.has(id)) {
        this.starredIds.delete(id)
      } else {
        this.starredIds.add(id)
      }
      saveStarred(Array.from(this.starredIds))
    },

    isStarred(id: number): boolean {
      return this.starredIds.has(id)
    },

    // ===== Базовые серверные действия =====

    /** После успешного запроса синхронизирует локальный item и счётчик totalUnread. */
    async markOneAsRead(id: number): Promise<void> {
      await notificationsApi.markOneRead(id)
      const item = this.items.find(n => n.id === id)
      if (item && !item.isSeen) {
        item.isSeen = true
        item.readAt = new Date().toISOString()
        if (this.totalUnread !== null && this.totalUnread > 0) {
          this.totalUnread -= 1
        }
      }
    },

    /** Проставляет isSeen=true всем элементам массива и обнуляет totalUnread. */
    async markAllAsRead(): Promise<void> {
      await notificationsApi.readAll()
      const now = new Date().toISOString()
      this.items.forEach(item => {
        if (!item.isSeen) {
          item.isSeen = true
          item.readAt = now
        }
      })
      this.totalUnread = 0
    },

    /** Удаляет элемент из items и selectedIds, корректирует счётчик totalUnread. */
    async remove(id: number): Promise<void> {
      await notificationsApi.remove(id)
      const removed = this.items.find(n => n.id === id)
      this.items = this.items.filter(n => n.id !== id)
      this.selectedIds.delete(id)
      if (this.totalCount > 0) {
        this.totalCount -= 1
      }
      if (removed && !removed.isSeen && this.totalUnread !== null && this.totalUnread > 0) {
        this.totalUnread -= 1
      }
    },

    // ===== Centrifuge realtime =====

    /**
     * Обработчик realtime-событий из единого канала user:user#sub.
     * Обрабатывает `new_notification` и `notification_updated`.
     *
     * - new_notification: добавляет новое уведомление в список и увеличивает счётчик totalUnread.
     * - notification_updated: обновляет данные существующего уведомления (кнопки, статус и т.д.).
     */
    handleRealtimeEvent(payload: IRealtimePayload): void {
      if (payload?.event_type !== 'new_notification' && payload?.event_type !== 'notification_updated') return
      if (!payload.data) return

      const dto = payload.data as INotificationDto

      if (payload.event_type === 'notification_updated') {
        const index = this.items.findIndex(item => item.id === dto.id)
        if (index !== -1) {
          const oldItem = this.items[index]
          if (oldItem.isSeen !== dto.is_read && this.totalUnread !== null) {
            if (dto.is_read) {
              this.totalUnread = Math.max(0, this.totalUnread - 1)
            } else {
              this.totalUnread += 1
            }
          }
          this.items[index] = mapDtoToVm(dto)
        }
        return
      }

      if (this.items.some(item => item.id === dto.id)) return

      if (!dto.is_read) {
        this.totalUnread = (this.totalUnread ?? 0) + 1
      }

      if (!dtoMatchesFilter(dto, this.filter)) return

      if (this.priority && dto.priority !== this.priority) return

      this.totalCount += 1
      if (this.currentPage !== 1) return

      this.items.unshift(mapDtoToVm(dto))
      if (this.items.length > this.itemsPerPage) {
        this.items.pop()
      }
    },
  },
})
